import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Skeleton, TextField, Typography } from '@mui/material';
import apiService, { MemberSignupDate } from '../../../services/api';
import { SummaryQuestion, SurveyResponseRow } from '../types';

/**
 * Individual responses, one card per member.
 *
 * A card rather than a wide table on purpose: there are 9 questions plus free
 * text, and a 12-column table forces horizontal scrolling and makes a single
 * person's answers impossible to read as a set. The CSV covers the spreadsheet
 * use case.
 */

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

/** Day only — the signup/activity dates don't warrant a time of day. */
const fmtDay = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
};

type MemberDates = Pick<MemberSignupDate, 'created_at' | 'first_app_activity_at'>;

/** Signup / first-activity line under the email. Both come from Aurora, not the survey store. */
const MemberDateLine: React.FC<{ dates?: MemberDates }> = ({ dates }) => (
  <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#6B7280' }}>
      Signed up:{' '}
      <Box component="span" sx={{ color: '#1F2937', fontWeight: 600 }}>
        {fmtDay(dates?.created_at)}
      </Box>
    </Typography>
    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#6B7280' }}>
      First app activity:{' '}
      <Box component="span" sx={{ color: '#1F2937', fontWeight: 600 }}>
        {fmtDay(dates?.first_app_activity_at)}
      </Box>
    </Typography>
  </Box>
);

interface Props {
  refreshKey?: number;
  /** Which survey to show. Null while the switcher is still loading. */
  campaign?: string | null;
}

export const ResponsesTab: React.FC<Props> = ({ refreshKey = 0, campaign = null }) => {
  const [rows, setRows] = useState<SurveyResponseRow[]>([]);
  const [questions, setQuestions] = useState<SummaryQuestion[]>([]);
  const [memberDates, setMemberDates] = useState<Map<string, MemberDates>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The summary is fetched for its question prompts and option labels, so this
      // tab shows "Better than I expected" rather than the raw id.
      const [r, s] = await Promise.all([
        apiService.getSurveyResponses({ campaign: campaign || undefined, limit: 500 }),
        apiService.getSurveySummary(campaign || undefined),
      ]);
      const responses: SurveyResponseRow[] = r?.responses || [];
      setRows(responses);
      setQuestions(s?.questions || []);

      // Signup / first-activity dates come from Aurora, keyed on email — the
      // survey store itself holds no member record. Failure here must not take
      // the responses down with it.
      const emails = [...new Set(responses.map((row) => String(row.email || '').toLowerCase()))]
        .filter(Boolean)
        .slice(0, 500);
      if (emails.length > 0) {
        try {
          const dates = await apiService.getMemberSignupDates(emails);
          const map = new Map<string, MemberDates>();
          for (const m of dates?.members || []) {
            map.set(String(m.email).toLowerCase(), {
              created_at: m.created_at ?? null,
              first_app_activity_at: m.first_app_activity_at ?? null,
            });
          }
          setMemberDates(map);
        } catch {
          setMemberDates(new Map());
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Could not load responses');
    }
    setLoading(false);
    // `campaign` MUST be here. Without it the memoised loader captures whatever the
    // prop was on first render — null, before the switcher has resolved — so every
    // later fetch went out unscoped and the Lambda served the default campaign. The
    // effect below re-fired on a switch and called this same stale function.
  }, [campaign]);

  useEffect(() => {
    load();
  }, [load, refreshKey, campaign]);

  /** option id → human label, across every question. */
  const labelFor = useMemo(() => {
    const map = new Map<string, string>();
    for (const q of questions) {
      for (const o of q.options || []) map.set(`${q.key}:${o.id}`, o.label);
    }
    return map;
  }, [questions]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    // Searches the answers too, not just the address — "price" should find the
    // people who said the price was too high.
    return rows.filter((r) =>
      Object.values(r).some((v) => String(v).toLowerCase().includes(term))
    );
  }, [rows, search]);

  const download = async () => {
    setDownloading(true);
    try {
      await apiService.exportSurveyResponses(campaign || undefined);
    } catch (e: any) {
      setError(e?.message || 'Download failed');
    }
    setDownloading(false);
  };

  if (loading) {
    return (
      <Box>
        {[0, 1].map((i) => (
          <Skeleton key={i} variant="rectangular" height={220} sx={{ borderRadius: 2, mb: 2 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search answers or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280, '& .MuiInputBase-input': { fontFamily: "'Montserrat', sans-serif", fontSize: '13px' } }}
        />
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#6B7280' }}>
          {filtered.length} of {rows.length}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          onClick={download}
          disabled={downloading || rows.length === 0}
          sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 600, textTransform: 'none', borderColor: '#1A5F7A', color: '#1A5F7A' }}
        >
          {downloading ? 'Preparing…' : 'Download CSV'}
        </Button>
      </Box>

      {error && (
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: '#7D1935', mb: 1.5 }}>
          {error}
        </Typography>
      )}

      {rows.length > 0 && (
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#9CA3AF', mb: 2 }}>
          “Signed up” is the member’s account creation date. “First app activity” is the earliest
          event Singular recorded for them — it is not an install date, and only covers activity
          from 12 June 2026 onward, so it reads “—” for most members.
        </Typography>
      )}

      {filtered.length === 0 && (
        <Paper sx={{ p: 4, borderRadius: '14px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', color: '#6B7280' }}>
            {rows.length === 0
              ? 'No responses yet.'
              : 'Nothing matches that search.'}
          </Typography>
        </Paper>
      )}

      {filtered.map((r) => (
        <Paper key={String(r.response_id)} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', fontWeight: 700, color: '#111827' }}>
              {r.email}
            </Typography>
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#6B7280' }}>
              {fmtDate(String(r.submitted_at))}
              {r.identified_via ? ` · ${String(r.identified_via).replace('_', ' ')}` : ''}
            </Typography>
          </Box>

          <MemberDateLine dates={memberDates.get(String(r.email || '').toLowerCase())} />

          {questions.map((q) => {
            const raw = String(r[q.key] || '').trim();
            const other = String(r[`${q.key}_other`] || '').trim();
            const isText = q.type === 'text';
            const textValue = isText ? String(r[q.key] || '').trim() : '';
            if (!raw && !other && !textValue) return null;

            // Text questions store their prose in r[q.key] itself, so treating that
            // as an option id list would print the answer twice — once unquoted
            // here and again as the verbatim quote below.
            const answers =
              !isText && raw ? raw.split(' | ').map((id) => labelFor.get(`${q.key}:${id}`) || id) : [];

            return (
              <Box key={q.key} sx={{ mb: 1.2 }}>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: '#9CA3AF' }}>
                  {q.prompt}
                </Typography>
                {answers.length > 0 && (
                  <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13.5px', color: '#1F2937' }}>
                    {answers.join(' · ')}
                  </Typography>
                )}
                {(other || textValue) && (
                  <Typography sx={{ fontFamily: "'Lora', Georgia, serif", fontSize: '14px', lineHeight: 1.6, color: '#1F2937', whiteSpace: 'pre-wrap', mt: 0.3 }}>
                    “{other || textValue}”
                  </Typography>
                )}
              </Box>
            );
          })}
        </Paper>
      ))}
    </Box>
  );
};

export default ResponsesTab;
